import { ChunkDataProvider, SceneId } from '../api';
import {
  ColliderData,
  CommonRegistry,
  TransformData,
  VelocityData,
} from '../components/components';
import { calculateCornerCorrection, checkMapCollision } from '../mapCollisionUtils';
import { World } from '../World';
import {
  CommonFilteredComponentSystem,
  FilteredComponentSystemUpdateEntityArgs,
} from './SystemBase';

/**
 * Handles movement and collision for fast-moving entities using substepping.
 * This prevents fast entities from "tunneling" through thin walls.
 */
export class FastMovementSystem extends CommonFilteredComponentSystem {
  public componentClasses: (keyof CommonRegistry)[] = [
    'Transform',
    'Velocity',
    'Collider',
    'FastMovement',
  ];

  protected simulationBound = true;

  /**
   * Override in subclasses to provide chunk data access.
   */
  protected getChunkDataProvider(): ChunkDataProvider | undefined {
    return undefined;
  }

  updateEntity({
    world,
    entityId,
    deltaTime,
  }: FilteredComponentSystemUpdateEntityArgs<CommonRegistry>): void {
    const transform = world.getComponent(entityId, 'Transform')!;
    const vel = world.getComponent(entityId, 'Velocity')!;
    const collider = world.getComponent(entityId, 'Collider')!;

    const provider = this.getChunkDataProvider();
    if (!provider) {
      this.integrateSimple(entityId, transform, vel, deltaTime);
      return;
    }

    const totalDx = vel.dx * deltaTime;
    const totalDy = vel.dy * deltaTime;
    const totalDistanceSq = totalDx * totalDx + totalDy * totalDy;

    if (totalDistanceSq <= 0) return;

    // Substepping: ensure we move no more than 4px at a time to stay well within tile midpoints.
    // This allows the collision engine to resolve overlaps correctly without "teleporting"
    // the entity past a wall's centerline.
    const MAX_STEP_SIZE = 4;
    const totalDistance = Math.sqrt(totalDistanceSq);
    const stepCount = Math.ceil(totalDistance / MAX_STEP_SIZE);
    const subDeltaTime = deltaTime / stepCount;

    let currentX = transform.x;
    let currentY = transform.y;
    let currentDx = vel.dx;
    let currentDy = vel.dy;

    for (let i = 0; i < stepCount; i++) {
      // 1. Integrate position
      currentX += currentDx * subDeltaTime;
      currentY += currentDy * subDeltaTime;

      // 2. Resolve collisions for this sub-step
      const resolution = this.resolveStepCollisions({
        world,
        entityId,
        provider,
        sceneId: transform.sceneId,
        x: currentX,
        y: currentY,
        dx: currentDx,
        dy: currentDy,
        collider,
        subDeltaTime,
      });

      currentX = resolution.x;
      currentY = resolution.y;
      currentDx = resolution.dx;
      currentDy = resolution.dy;
    }

    // Apply final results
    this.updateComponent(entityId, transform, { x: currentX, y: currentY });

    if (currentDx !== vel.dx || currentDy !== vel.dy) {
      this.updateComponent(entityId, vel, { dx: currentDx, dy: currentDy });
    }
  }

  /**
   * Fallback for when no map data is available. Just integrate velocity into transform.
   */
  private integrateSimple(
    entityId: number,
    transform: { readonly typeName: 'Transform' } & TransformData,
    vel: VelocityData,
    deltaTime: number,
  ): void {
    const newX = transform.x + vel.dx * deltaTime;
    const newY = transform.y + vel.dy * deltaTime;
    this.updateComponent(entityId, transform, { x: newX, y: newY });
  }

  /**
   * Iteratively checks and resolves collisions for a single sub-step.
   * Fast entities can overlap multiple tiles; iterative resolution ensures
   * that all obstacles are cleared before the next movement step.
   */
  private resolveStepCollisions({
    world,
    entityId,
    provider,
    sceneId,
    x,
    y,
    dx,
    dy,
    collider,
    subDeltaTime,
  }: {
    world: World<CommonRegistry>;
    entityId: number;
    provider: ChunkDataProvider;
    sceneId: SceneId;
    x: number;
    y: number;
    dx: number;
    dy: number;
    collider: ColliderData;
    subDeltaTime: number;
  }): { x: number; y: number; dx: number; dy: number } {
    const EPSILON = 0.001;
    const MAX_RESOLVE_ITERATIONS = 4;

    let currentX = x;
    let currentY = y;
    let currentDx = dx;
    let currentDy = dy;

    for (let resolveAttempt = 0; resolveAttempt < MAX_RESOLVE_ITERATIONS; resolveAttempt++) {
      const entityRect = {
        x: currentX + collider.offsetX,
        y: currentY + collider.offsetY,
        width: collider.width,
        height: collider.height,
      };

      const result = checkMapCollision(provider, entityRect, sceneId);
      if (!result || !result.mtv) break;

      const mtv = result.mtv;

      // Resolve overlap with a tiny buffer to avoid precision-based re-collisions
      currentX += mtv.x + (mtv.x !== 0 ? Math.sign(mtv.x) : 0) * EPSILON;
      currentY += mtv.y + (mtv.y !== 0 ? Math.sign(mtv.y) : 0) * EPSILON;

      // Zero out velocity components that oppose the resolution vector
      if ((mtv.x > 0 && currentDx < 0) || (mtv.x < 0 && currentDx > 0)) {
        currentDx = 0;
      }
      if ((mtv.y > 0 && currentDy < 0) || (mtv.y < 0 && currentDy > 0)) {
        currentDy = 0;
      }

      // Corner correction: slide around corners if movement is mostly cardinal
      const correction = calculateCornerCorrection({
        provider,
        sceneId,
        entityRect: {
          x: currentX + collider.offsetX,
          y: currentY + collider.offsetY,
          width: collider.width,
          height: collider.height,
        },
        collidingTiles: result.collidingTiles,
        mtv,
        vel: { dx: currentDx, dy: currentDy },
        entitySpeed: this.services.speed.getEffectiveSpeed(world, entityId),
        deltaTime: subDeltaTime,
      });

      if (correction) {
        currentX += correction.x;
        currentY += correction.y;
      }
    }

    return { x: currentX, y: currentY, dx: currentDx, dy: currentDy };
  }
}
